// Complete implementation with PDF text extraction and GPT-based comparison
import { useState, useEffect } from "react";
import {
  Calendar,
  Filter,
  Search,
  Download,
  FileText,
  BarChart2,
  Camera,
  Upload,
  Share,
} from "lucide-react";
import { LabReportAIAnalysis } from "../ScanReports/LabReportAIAnalysis";
import { LabReportComparison } from "../ScanReports/LabReportComparison";
import authService from "../../../Services/authService";
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;


const OPENAI_API_KEY = "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"; // Replace with secure handling in production

export default function HealthReports() {
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState({ id: "", title: "", imagepath: "" });
  const [selectedReportsForComparison, setSelectedReportsForComparison] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [Data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    loadReports(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const loadReports = (page = 1, perPage = pageSize) => {
    const token = authService.getCurrentUsertoken();

    fetch(`${authService.getApiUrl()}myreports`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        page,
        per_page: perPage,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.status === 401 ? (authService.clientlogout(), window.location.href = "/admin") : res.json())
      .then(result => {
        setData(result.data);
        setTotalRecords(result.data?.total || 0);
      })
      .catch(console.error);
  };

  const extractTextFromPDF = async (originalUrl) => {
    const proxyUrl = `https://api.Pranaam365.com/api/proxy-pdf?url=${encodeURIComponent(originalUrl)}`;
    const loadingTask = pdfjsLib.getDocument(proxyUrl);
    const pdf = await loadingTask.promise;

    let fullText = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const textItems = textContent.items.map(item => item.str);
      fullText += textItems.join(" ") + "\n";
    }
    return fullText;
  };



  const compareWithGPT = async (text1, text2) => {
    const messages = [
      { role: "system", content: "Compare the following two medical reports and highlight key differences in lab values, tests, or findings." },
      { role: "user", content: `Report A:\n${text1}` },
      { role: "user", content: `Report B:\n${text2}` },
    ];

    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-4",
        messages,
        temperature: 0.3,
      }),
    });

    const result = await response.json();
    return result.choices?.[0]?.message?.content || "Comparison failed.";
  };

  // const startComparison = async () => {
  //   if (selectedReportsForComparison.length !== 2) return;

  //   const [reportA, reportB] = selectedReportsForComparison;
  //   const urlA = `${authService.getApiDomainUrl()}${reportA.document_name}`;
  //   const urlB = `${authService.getApiDomainUrl()}${reportB.document_name}`;

  //   console.log("Comparing PDFs:", urlA, urlB);

  //   try {
  //     const textA = await extractTextFromPDF(urlA);
  //     const textB = await extractTextFromPDF(urlB);

  //     console.log("Extracted Text A:", textA.slice(0, 300));
  //     console.log("Extracted Text B:", textB.slice(0, 300));

  //     const comparison = await compareWithGPT(textA, textB);
  //     console.log("GPT Comparison Result:", comparison);
  //     alert("Comparison Result:\n" + comparison);
  //   } catch (error) {
  //     console.error("Comparison error:", error);
  //     alert("Failed to compare reports.");
  //   }
  // };


  const toggleComparisonMode = () => {
    setComparisonMode(!comparisonMode);
    setSelectedReportsForComparison([]);
  };

  const toggleReportSelection = (report) => {
    const id = report.id.toString();
    const isSelected = selectedReportsForComparison.some(r => r.id.toString() === id);
    if (isSelected) {
      setSelectedReportsForComparison(prev => prev.filter(r => r.id.toString() !== id));
    } else if (selectedReportsForComparison.length < 2) {
      setSelectedReportsForComparison(prev => [...prev, report]);
    } else {
      setSelectedReportsForComparison([selectedReportsForComparison[1], report]);
    }
  };

  const openAnalysis = (id, title, image) => {
    setSelectedReport({ id: String(id), title, imagepath: String(image) });
    setAnalysisOpen(true);
  };

  const renderBrainIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="brain-icon"
    >
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"></path>
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"></path>
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"></path>
      <path d="M17.599 6.5a3 3 0 0 0 .399-1.375"></path>
      <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"></path>
      <path d="M3.477 10.896a4 4 0 0 1 .585-.396"></path>
      <path d="M19.938 10.5a4 4 0 0 1 .585.396"></path>
      <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
      <path d="M19.967 17.484A4 4 0 0 1 18 18"></path>
    </svg>
  );

  const startCompare = () => {
    if (selectedReportsForComparison.length === 2) {
      setComparisonOpen(true);
    }
  };


  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <FileText />
          <h1>Health Reports</h1>
        </div>
        <div className="comparison-toggle">
          <button
            className={`button ${comparisonMode ? "button-default" : "button-outline"}`}
            onClick={toggleComparisonMode}
          >
            <BarChart2 className="mr-2 h-4 w-4" />
            {comparisonMode ? "Cancel Comparison" : "Compare Reports"}
          </button>
        </div>
      </div>

      {comparisonMode && (
        <div className="comparison-banner mb-4 p-3 bg-blue-50 rounded-md flex justify-between items-center">
          <div>
            <p className="text-blue-800 font-medium">
              {selectedReportsForComparison.length === 0
                ? "Select two reports to compare"
                : selectedReportsForComparison.length === 1
                  ? "Select one more report to compare"
                  : "Ready to compare selected reports"}
            </p>
            {selectedReportsForComparison.length > 0 && (
              <p className="text-blue-600 text-sm">
                Selected: {selectedReportsForComparison.map((r) => `Report #${r.id} (${r.date})`).join(" and ")}
              </p>
            )}
          </div>
          <button
            className="button button-default"
            disabled={selectedReportsForComparison.length !== 2}
            //onClick={startComparison}
            onClick={startCompare}
          >
            Compare Selected
          </button>
        </div>
      )}

      <div className="search-container">
        <div className="search-input">
          <Search className="search-icon" />
          <input type="text" placeholder="Search documents..." />
        </div>
        {/* <div className="filter-group">
                <button className="filter-button">
                  <Filter />
                  <span>Filter</span>
                </button>
                <button className="filter-button">
                  <Calendar />
                  <span>Date</span>
                </button>
              </div> */}
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {comparisonMode && <th style={{ width: "50px" }}>Select</th>}
              <th>Phone Number</th>
              <th>Date</th>
              <th>Download</th>
              <th>AI Analysis</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            {Data?.data?.map((report) => {
              const id = report.id.toString();
              const date = new Date(report.created_at).toLocaleDateString();
              const title = `Report #${id}`;
              const phone = report.phone_number;
              const documentUrl = `${authService.getApiDomainUrl()}${report.document_name}`;

              const isSelected = selectedReportsForComparison.some((r) => r.id.toString() === id);

              return (
                <tr
                  key={id}
                  className={comparisonMode && isSelected ? "bg-blue-50" : ""}
                  onClick={() => comparisonMode && toggleReportSelection({ ...report, document_name: report.document_name })}
                  style={{ cursor: comparisonMode ? "pointer" : "default" }}
                >
                  {comparisonMode && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleReportSelection({ ...report, document_name: report.document_name })}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </td>
                  )}
                  <td>{phone}</td>
                  <td>{date}</td>
                  <td>
                    <a
                      href={documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download className="action-icon" />
                    </a>
                  </td>
                  <td>
                    <button
                      className="brain-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAnalysis(id, title, documentUrl);
                      }}
                    >
                      {renderBrainIcon()}
                    </button>
                  </td>
                  <td>
                    <button
                      className="share-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Share logic here
                      }}
                    >
                      <Share size={14} />
                      <span>Share</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>

      <LabReportAIAnalysis
        isOpen={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        reportTitle="Report"
        reportId={selectedReport.id}
        reportFile={selectedReport.imagepath}
      />

      <LabReportComparison
        isOpen={comparisonOpen}
        onClose={() => setComparisonOpen(false)}
        selectedReports={selectedReportsForComparison}
      />
    </div>
  );
}
